import { useEffect, useState } from "react";
import { X } from "lucide-react";

export const Card = ({ className = "", children, ...rest }) => (
  <div className={`rounded-2xl border border-line bg-white p-5 shadow-soft ${className}`} {...rest}>
    {children}
  </div>
);

export const Stat = ({ label, value, hint, testId }) => (
  <Card data-testid={testId}>
    <p className="text-sm font-medium text-body">{label}</p>
    <p className="mt-2 text-3xl font-bold tracking-tight text-navy">{value}</p>
    {hint && <p className="mt-1 text-xs text-body">{hint}</p>}
  </Card>
);

export const Badge = ({ map, value, testId }) => {
  const s = map[value] || { label: value, cls: "bg-alt text-navy" };
  return (
    <span data-testid={testId} className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
};

const base = "inline-flex min-h-12 items-center justify-center gap-2 rounded-xl px-5 text-base font-semibold transition-colors duration-200 disabled:opacity-50";
const variants = {
  brand: "bg-brand text-white hover:bg-brand-dark",
  outline: "border border-line bg-white text-navy hover:border-brand hover:text-brand",
  ghost: "text-navy hover:bg-alt",
  danger: "border border-red-200 bg-white text-red-700 hover:bg-red-50",
  whatsapp: "bg-[#25D366] text-white hover:bg-[#1ebe5b]",
};
export const Btn = ({ variant = "brand", className = "", as: Tag = "button", ...rest }) => (
  <Tag className={`${base} ${variants[variant]} ${className}`} {...(Tag === "button" ? { type: rest.type || "button" } : {})} {...rest} />
);

export const inputCls = "min-h-12 w-full rounded-xl border border-line bg-white px-4 text-base text-navy outline-none transition-colors focus:border-brand";

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-navy">{label}</span>
    {children}
  </label>
);

export const Modal = ({ title, onClose, children, testId }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-navy/60 p-0 sm:items-center sm:p-6" onClick={onClose}>
      <div
        data-testid={testId}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white p-6 shadow-soft sm:max-w-lg sm:rounded-2xl"
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="text-xl font-bold text-navy">{title}</h2>
          <button type="button" aria-label="Close" data-testid="modal-close" onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-alt">
            <X size={22} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const Empty = ({ children, testId }) => (
  <Card data-testid={testId} className="text-center text-body">
    {children}
  </Card>
);

export const useAsync = (fn, deps) => {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const run = async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      setState({ data: await fn(), error: null, loading: false });
    } catch (e) {
      setState({ data: null, error: e.message, loading: false });
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => void run(), deps);
  return { ...state, reload: run };
};

export const Loading = () => <p className="py-10 text-center text-body">Loading…</p>;
export const ErrorMsg = ({ error }) => (error ? <p data-testid="error-message" className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{error}</p> : null);

/** Resize an image file in the browser to max 1600px and return a JPEG data URL. */
export const fileToDataUrl = (file, max = 1600) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => reject(new Error("That file is not an image we can read"));
    img.src = url;
  });
